import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, MapPin, Clock, Star, ChevronLeft, ImageIcon, Upload, Trophy, Pencil, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ActionBar } from "@/components/ActionBar";
import { StarRating } from "@/components/StarRating";
import { PlaceCard } from "@/components/PlaceCard";
import { CATEGORY_COLORS, SportIcon } from "@/components/CategoryIcon";
import { SPORTS_TAGS } from "@/lib/constants";
import { getOpenStatus, todayKey, getBusyHint, dayLabel } from "@/lib/hours";
import { recurrenceLabel, nextOccurrences } from "@/lib/events";

const DAYS = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];

export default function PlaceDetail() {
  const { slug } = useParams();
  const { user, refreshUser } = useAuth();
  const [place, setPlace] = useState(null);
  const [gReviews, setGReviews] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [related, setRelated] = useState([]);
  const [activePhoto, setActivePhoto] = useState(0);
  const [form, setForm] = useState({ rating: 5, title: "", body: "", photo: "" });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!user) return toast.error("Sign in to add a photo");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      setForm((f) => ({ ...f, photo: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }));
      toast.success("Photo added");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const load = async () => {
    const { data } = await api.get(`/places/${slug}`);
    setPlace(data);
    api.get(`/places/${data.id}/google-reviews`).then((r) => setGReviews(r.data));
    api.get("/reviews", { params: { place_id: data.id } }).then((r) => setReviews(r.data));
    api.get("/places", { params: { category: data.category } }).then((r) =>
      setRelated(r.data.filter((p) => p.id !== data.id).slice(0, 3)));
  };

  useEffect(() => { window.scrollTo(0, 0); load(); }, [slug]);

  if (!place) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;

  const saved = user && user.saved_places?.includes(place.id);
  const photos = place.photos?.length ? place.photos : [place.cover_image];
  const color = CATEGORY_COLORS[place.category];

  const toggleSave = async () => {
    if (!user) return toast.error("Sign in to save favorites");
    await api.post(`/favorites/places/${place.id}`);
    await refreshUser();
    toast.success(saved ? "Removed from saved" : "Saved to favorites");
  };

  const toggleHidden = async () => {
    try {
      const next = !place.is_hidden;
      await api.put(`/places/${place.id}`, { is_hidden: next });
      setPlace((p) => ({ ...p, is_hidden: next }));
      toast.success(next ? "Hidden from the site" : "Now visible on the site");
    } catch { toast.error("Couldn't update visibility"); }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Sign in to write a review");
    setSubmitting(true);
    try {
      const { photo, ...rest } = form;
      await api.post("/reviews", { place_id: place.id, ...rest, photos: photo ? [photo] : [] });
      toast.success("Review submitted! It'll appear once approved.");
      setForm({ rating: 5, title: "", body: "", photo: "" });
    } catch {
      toast.error("Could not submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pb-16">
      {/* Hero gallery */}
      <div className="relative">
        <img src={photos[activePhoto]} alt={place.name} className="max-h-[80vh] min-h-[42vh] w-full object-cover md:h-[62vh]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20" />
        <Link to="/explore" className="absolute left-4 top-4 flex items-center gap-1 rounded-full bg-black/40 px-3 py-2 text-sm font-medium text-white backdrop-blur-sm">
          <ChevronLeft size={16} /> Back
        </Link>
        <div className="absolute right-4 top-4 flex items-center gap-2">
          {user && (user.role === "admin" || user.role === "editor") && (
            <>
              <button onClick={toggleHidden} data-testid="hide-place-btn"
                className="flex items-center gap-1.5 rounded-full bg-white/90 px-4 py-2.5 text-sm font-semibold text-foreground shadow-sm transition-transform hover:scale-105">
                {place.is_hidden ? <><Eye size={16} /> Show</> : <><EyeOff size={16} /> Hide</>}
              </button>
              <Link to={`/admin?editPlace=${place.slug}`} data-testid="edit-place-btn"
                className="flex items-center gap-1.5 rounded-full bg-white/90 px-4 py-2.5 text-sm font-semibold text-primary shadow-sm transition-transform hover:scale-105">
                <Pencil size={16} /> Edit
              </Link>
            </>
          )}
          <button onClick={toggleSave} data-testid="save-place-btn"
            className="flex h-11 w-11 items-center justify-center rounded-full bg-white/90 text-primary transition-transform hover:scale-110">
            <Heart size={20} className={saved ? "fill-primary" : ""} />
          </button>
        </div>
        <div className="absolute bottom-0 left-0 right-0 mx-auto max-w-5xl px-4 pb-6 md:px-8">
          <span className="mb-2 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold text-white" style={{ backgroundColor: `${color}dd` }}>
            {place.category}{place.sub_category ? ` · ${place.sub_category}` : ""}
          </span>
          <h1 className="font-serif text-4xl font-bold text-white text-shadow-soft md:text-5xl">{place.name}</h1>
          <p className="mt-1 flex items-center gap-3 text-white/85 text-shadow-soft">
            <span className="flex items-center gap-1"><MapPin size={15} /> {place.city}, {place.state}</span>
            {place.price_range && <span>{place.price_range}</span>}
          </p>
        </div>
      </div>

      {photos.length > 1 && (
        <div className="no-scrollbar mx-auto flex max-w-5xl gap-2 overflow-x-auto px-4 py-3 md:px-8">
          {photos.map((ph, i) => (
            <button key={i} onClick={() => setActivePhoto(i)}
              className={`h-16 w-24 flex-shrink-0 overflow-hidden rounded-lg border-2 ${activePhoto === i ? "border-primary" : "border-transparent"}`}>
              <img src={ph} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}

      <div className="mx-auto max-w-5xl px-4 md:px-8">
        {/* Action bar */}
        <div className="sticky top-16 z-20 -mx-4 bg-background/80 px-4 py-3 backdrop-blur-md md:mx-0 md:rounded-2xl md:px-4">
          <ActionBar place={place} />
        </div>

        {/* About + hours */}
        <div className="mt-8 grid gap-8 md:grid-cols-3">
          <div className="md:col-span-2">
            <h2 className="mb-3 font-serif text-2xl font-semibold">About</h2>
            <p className="leading-relaxed text-muted-foreground">{place.description}</p>
            {place.tags?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {place.tags.map((t) => (
                  <span key={t} className="rounded-full bg-secondary/10 px-3 py-1 text-xs font-medium text-secondary">#{t}</span>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-6">
            {(place.category === "Sports" || place.num_courts != null || place.access_type || (place.tags || []).some((t) => SPORTS_TAGS.includes(t))) && (
              <div data-testid="facility-details" className="rounded-2xl border border-border bg-card p-5">
                <h3 className="mb-3 flex items-center gap-2 font-semibold"><Trophy size={16} /> Facility</h3>
                <ul className="space-y-2 text-sm">
                  {place.num_courts != null && <li className="flex items-center justify-between"><span className="text-muted-foreground">Courts / fields</span><span className="font-medium">{place.num_courts}</span></li>}
                  {place.access_type && <li className="flex items-center justify-between"><span className="text-muted-foreground">Access</span><span className="font-medium capitalize">{place.access_type}</span></li>}
                  {(place.tags || []).filter((t) => SPORTS_TAGS.includes(t)).length > 0 && (
                    <li className="flex items-start justify-between gap-2"><span className="text-muted-foreground">Sports</span>
                      <span className="flex flex-wrap justify-end gap-1">
                        {(place.tags || []).filter((t) => SPORTS_TAGS.includes(t)).map((s) => (
                          <span key={s} className="flex items-center gap-1 rounded-full bg-secondary/10 px-2 py-0.5 text-xs font-medium text-secondary"><SportIcon sport={s} className="h-3 w-3" /> {s}</span>
                        ))}
                      </span>
                    </li>
                  )}
                </ul>
                {place.reservation_url && <a href={place.reservation_url} target="_blank" rel="noreferrer" data-testid="facility-reserve" className="mt-3 flex items-center justify-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">Reserve / Book</a>}
              </div>
            )}
            <div className="rounded-2xl border border-border bg-card p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="flex items-center gap-2 font-semibold"><Clock size={16} /> Hours</h3>
              {(() => {
                const status = getOpenStatus(place.hours, place.special_hours);
                if (status.status === "unknown") return null;
                return (
                  <span
                    data-testid="place-open-status"
                    className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${status.closingSoon ? "bg-amber-100 text-amber-700" : status.status === "open" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}
                  >
                    <span className={`h-2 w-2 rounded-full ${status.closingSoon ? "bg-amber-500" : status.status === "open" ? "bg-green-500" : "bg-red-400"}`} />
                    {status.label}
                  </span>
                );
              })()}
            </div>
            {(() => {
              const status = getOpenStatus(place.hours, place.special_hours);
              if (status.status === "unknown") return null;
              return (
                <div data-testid="place-today-hours" className="mb-3 rounded-xl bg-secondary/10 px-3 py-2 text-sm">
                  <span className="font-semibold">Today</span>
                  <span className="ml-2 text-muted-foreground">{status.today}</span>
                  {status.detail && <span className="ml-1 text-muted-foreground">· {status.detail}</span>}
                  {status.closingSoon && <span className="ml-1 font-semibold text-amber-600">· {status.soonText}</span>}
                  {status.holiday && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">{status.holidayNote}</span>}
                </div>
              );
            })()}
            {(() => {
              const hint = getBusyHint(place);
              if (!hint) return null;
              return (
                <div data-testid="busy-hint" className="mb-3 flex items-center gap-1.5 text-xs">
                  <span className={`h-2 w-2 rounded-full ${hint.dot}`} />
                  <span className={`font-medium ${hint.color}`}>{hint.text}</span>
                  <span className="text-muted-foreground">· estimated</span>
                </div>
              );
            })()}
            {getOpenStatus(place.hours, place.special_hours).status === "unknown" ? (
              <p className="text-sm text-muted-foreground" data-testid="place-hours-unlisted">Hours not listed. Check the venue's website or call ahead.</p>
            ) : (
            <ul className="space-y-1.5 text-sm">
              {DAYS.map(([k, lbl]) => {
                const h = place.hours?.[k];
                const isToday = k === todayKey();
                return (
                  <li key={k} className={`flex justify-between ${isToday ? "font-semibold text-foreground" : ""}`}>
                    <span className={isToday ? "" : "text-muted-foreground"}>{lbl}{isToday ? " · Today" : ""}</span>
                    <span className="font-medium">{dayLabel(h)}</span>
                  </li>
                );
              })}
            </ul>
            )}
            {place.address && <p className="mt-4 border-t border-border pt-3 text-sm text-muted-foreground">{place.address}</p>}
          </div>
          </div>
        </div>

        {/* Google reviews */}
        {gReviews.length > 0 && (
          <section className="mt-10">
            <div className="mb-4 flex items-center gap-3">
              <h2 className="font-serif text-2xl font-semibold">Google Reviews</h2>
              {place.google_rating && (
                <span className="flex items-center gap-1 text-sm font-semibold">
                  <Star size={15} className="fill-accent text-accent" /> {place.google_rating.toFixed(1)}
                  <span className="text-muted-foreground">({place.google_review_count})</span>
                </span>
              )}
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {gReviews.map((r, i) => (
                <div key={i} className="rounded-2xl border border-border bg-card p-4" data-testid="google-review">
                  <div className="flex items-center gap-3">
                    <img src={r.profile_photo_url} alt={r.author_name} className="h-9 w-9 rounded-full" />
                    <div>
                      <p className="text-sm font-semibold">{r.author_name}</p>
                      <StarRating value={r.rating} />
                    </div>
                    <span className="ml-auto text-xs text-muted-foreground">{r.relative_time}</span>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{r.text}</p>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">Reviews from Google. CSRAGo is not affiliated with Google.</p>
          </section>
        )}

        {/* Community reviews */}
        <section className="mt-10">
          <h2 className="mb-4 font-serif text-2xl font-semibold">
            Community Reviews
            {place.community_review_count > 0 && (
              <span className="ml-2 text-base font-normal text-muted-foreground">
                ({place.average_community_rating.toFixed(1)} · {place.community_review_count})
              </span>
            )}
          </h2>

          {user ? (
            <form onSubmit={submitReview} className="mb-6 rounded-2xl border border-border bg-card p-5" data-testid="review-form">
              <div className="mb-3 flex items-center gap-2">
                <span className="text-sm font-medium">Your rating:</span>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })}>
                    <Star size={22} className={n <= form.rating ? "fill-accent text-accent" : "text-muted-foreground/40"} />
                  </button>
                ))}
              </div>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Title (optional)" data-testid="review-title"
                className="mb-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
              <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="Share your experience…" rows={3} required data-testid="review-body"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
              <div className="mt-2">
                <label className="flex w-fit cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border bg-background px-3 py-2 text-sm text-muted-foreground hover:border-primary">
                  <Upload size={15} /> {uploading ? "Uploading…" : form.photo ? "Change photo" : "Add a photo (optional)"}
                  <input type="file" accept="image/*" className="hidden" data-testid="review-photo" onChange={uploadPhoto} disabled={uploading} />
                </label>
                {form.photo && <img src={form.photo} alt="preview" className="mt-2 h-20 w-20 rounded-lg object-cover" />}
              </div>
              <button type="submit" disabled={submitting} data-testid="submit-review-btn"
                className="mt-3 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">
                {submitting ? "Submitting…" : "Post review"}
              </button>
            </form>
          ) : (
            <Link to="/auth" className="mb-6 block rounded-2xl border border-dashed border-border bg-card p-5 text-center text-sm text-muted-foreground">
              <span className="font-semibold text-primary">Sign in</span> to write a review
            </Link>
          )}

          {reviews.length ? (
            <div className="space-y-4">
              {reviews.map((r) => (
                <div key={r.id} className="rounded-2xl border border-border bg-card p-4" data-testid="community-review">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{r.user_name || "Local"}</span>
                    <StarRating value={r.rating} />
                    <span className="ml-auto text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                  {r.title && <p className="mt-1.5 font-medium">{r.title}</p>}
                  {r.body && <p className="mt-1 text-sm text-muted-foreground">{r.body}</p>}
                  {r.photos?.length > 0 && (
                    <div className="mt-2 flex gap-2">
                      {r.photos.map((ph, i) => (
                        <img key={i} src={ph} alt="" className="h-20 w-20 rounded-lg object-cover" />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="flex items-center gap-2 text-sm text-muted-foreground"><ImageIcon size={15} /> Be the first to review {place.name}.</p>
          )}
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="mb-4 font-serif text-2xl font-semibold">People also like</h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((p, i) => <PlaceCard key={p.id} place={p} index={i} />)}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
